import * as z from 'zod/mini';
import { OUTLET_MAX } from '../constants.ts';
import { idSchema, pathSchema, timestampSchema } from './common.ts';

// A power feed: one cord from an outlet of a UPS, PDU, or power strip to
// the device it powers. Outlets are numbered from 1 and are consumed one
// feed at a time, the way switch ports are; a device draws from a single
// outlet (a device with redundant power supplies is out of scope, so its
// second supply is not recorded). The rules that decide whether a feed may
// be made live in shared/power.ts, shared by the form and the Worker.

export const outletSchema = z.int().check(z.positive(), z.lte(OUTLET_MAX));

export const powerFeedSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  sourceItemId: idSchema,
  outlet: outletSchema,
  fedItemId: idSchema,
  createdAt: timestampSchema,
});
export type PowerFeed = z.infer<typeof powerFeedSchema>;

export const powerFeedInputSchema = z.strictObject({
  sourceItemId: idSchema,
  outlet: outletSchema,
  fedItemId: idSchema,
});
export type PowerFeedInput = z.infer<typeof powerFeedInputSchema>;

/** A feed with both of its devices named and placed, for lists and pages. */
export const powerFeedViewSchema = z.object({
  feed: powerFeedSchema,
  sourceName: z.string(),
  sourcePath: pathSchema,
  fedName: z.string(),
  fedPath: pathSchema,
});
export type PowerFeedView = z.infer<typeof powerFeedViewSchema>;

/** One outlet of a source and the device it feeds, or null when it is free. */
export const outletRowSchema = z.object({
  outlet: outletSchema,
  feedId: z.nullable(idSchema),
  fedItemId: z.nullable(idSchema),
  fedName: z.string(),
});
export type OutletRow = z.infer<typeof outletRowSchema>;

/** Where a device draws its power: the feed, the source, and the outlet. */
export const fedBySchema = z.object({
  feedId: idSchema,
  sourceItemId: idSchema,
  sourceName: z.string(),
  outlet: outletSchema,
});

/** GET /api/items/:id/power: what feeds this device, and what its outlets feed. */
export const itemPowerSchema = z.object({
  fedBy: z.nullable(fedBySchema),
  /**
   * Every outlet of a UPS, PDU, or power strip in order: the declared
   * count when it has one, otherwise only the outlets in use. Empty for a
   * device that is not a power source.
   */
  outlets: z.array(outletRowSchema),
});
export type ItemPower = z.infer<typeof itemPowerSchema>;

export const powerListSchema = z.object({ feeds: z.array(powerFeedViewSchema) });
export const powerFeedEnvelopeSchema = z.object({ feed: powerFeedSchema });
